import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { format, subDays } from 'date-fns';
import { TrendingUp, ArrowDown, Filter, LineChart, BarChart3 } from 'lucide-react';
import {
  ResponsiveContainer,
  LineChart as ReLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  LabelList,
  BarChart,
  Bar,
  Legend
} from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import ComplaintsNegativityChart from './ComplaintsNegativityChart';
import CallTopicsChart from './CallTopicsChart';
import ObjectionsChart from './ObjectionsChart';
import MesorealDashboardWidgets from './MesorealDashboardWidgets';
import ElectrobikeDashboardWidgets from './ElectrobikeDashboardWidgets';
import BelyMedvedDashboardWidgets from './BelyMedvedDashboardWidgets';
import DentalDepoDashboardWidgets from './DentalDepoDashboardWidgets';
import PlusDashboardWidgets from './PlusDashboardWidgets';
import { fetchCallDynamics, fetchBookingFunnel, fetchConversionDynamics, getCallClassificationTypes } from '../api';
import { GRAPH_REFRESH_INTERVAL_MS } from '../config/constants';
import useTimeZone from '../hooks/useTimeZone';
import { formatDayMonthYear } from '../utils/dateTime';
import { formatClassificationType } from '../utils/callClassification';

const defaultRange = {
  from: subDays(new Date(), 29),
  to: new Date()
};

const TENANT_ID = process.env.REACT_APP_TENANT_ID?.trim().toLowerCase();
const USE_CUSTOM_FUNNEL_TENANTS = new Set(['electrobike', 'bely_medved', 'dental_depo', 'plus']);

const callsAxisFormatter = (value) =>
  typeof value === 'number' ? value.toLocaleString('ru-RU') : value ?? '—';

const callDynamicsTooltipFormatter = (entry) => {
  if (!entry || typeof entry.value !== 'number') {
    return '—';
  }

  return `${entry.value.toLocaleString('ru-RU')}`;
};

const createValueLabelRenderer = (color, formatter) => (props) => {
  const { x, y, value, width } = props;
  if (value === null || value === undefined) {
    return null;
  }

  const baseX = typeof x === 'number' ? x : 0;
  const centerX = typeof width === 'number' ? baseX + width / 2 : baseX;

  return (
    <text
      x={centerX}
      y={(y ?? 0) - 8}
      textAnchor="middle"
      fill={color}
      fontSize="9"
      fontWeight="600"
    >
      {formatter ? formatter(value) : value}
    </text>
  );
};

const tooltipWrapperStyle = { zIndex: 30 };

const formatCallDynamicsLabel = (point, timeZone) => {
  if (!point) {
    return '—';
  }

  const granularity = point.granularity?.toLowerCase();
  if (granularity === 'hour' || granularity === 'minute') {
    return point.label ?? '—';
  }

  const raw = point.day ?? point.label;
  if (!raw) {
    return '—';
  }

  if (raw instanceof Date || typeof raw === 'string') {
    const formatted = formatDayMonthYear(raw, timeZone);
    return formatted === '—' ? String(raw) : formatted;
  }

  return typeof raw === 'string' ? raw : String(raw);
};

const ChartTooltip = ({ active, payload, label, formatter }) => {
  if (!active || !payload?.length) {
    return null;
  }

  return (
    <div
      className="rounded-md border px-3 py-2 text-sm shadow-md"
      style={{
        backgroundColor: 'hsl(var(--card))',
        color: 'hsl(var(--card-foreground))',
        borderColor: 'hsl(var(--border))'
      }}
    >
      <div className="mb-1 font-medium">{label}</div>
      {payload.map((entry) => (
        <div key={entry.dataKey} className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: entry.color }} />
          <span>
            {entry.name ?? entry.dataKey}: {formatter ? formatter(entry) : entry.value}
          </span>
        </div>
      ))}
    </div>
  );
};

const GeneralStatistics = ({ dateRange, callType = 'all', onOpenCallJournal }) => {
  const [chartType, setChartType] = useState('bar');
  const [callDynamics, setCallDynamics] = useState([]);
  const [classificationTypes, setClassificationTypes] = useState([]);
  const [selectedClassificationType, setSelectedClassificationType] = useState('');
  const [bookingFunnel, setBookingFunnel] = useState(null);
  const [conversionDynamics, setConversionDynamics] = useState([]);
  const [isLoadingDynamics, setIsLoadingDynamics] = useState(false);
  const [isLoadingFunnel, setIsLoadingFunnel] = useState(false);
  const [error, setError] = useState(null);
  const isFetchingDynamics = useRef(false);
  const isFetchingFunnel = useRef(false);
  const isFetchingConversion = useRef(false);
  const { timeZone } = useTimeZone();

  const activeDateRange = useMemo(() => {
    if (dateRange?.from && dateRange?.to) {
      return dateRange;
    }

    return defaultRange;
  }, [dateRange]);

  const dateRangeLabel = useMemo(() => {
    if (!activeDateRange?.from || !activeDateRange?.to) {
      return 'за выбранный период';
    }

    try {
      const fromLabel = formatDayMonthYear(activeDateRange.from, timeZone);
      const toLabel = formatDayMonthYear(activeDateRange.to, timeZone);
      return `за период ${fromLabel} — ${toLabel}`;
    } catch {
      return 'за выбранный период';
    }
  }, [activeDateRange, timeZone]);

  const loadClassificationTypes = useCallback(async () => {
    try {
      const data = await getCallClassificationTypes();
      setClassificationTypes(Array.isArray(data) ? data : []);
    } catch {
      setClassificationTypes([]);
    }
  }, []);

  const loadCallDynamics = useCallback(async (showSpinner = true) => {
    if (!activeDateRange?.from || !activeDateRange?.to || isFetchingDynamics.current) {
      return;
    }

    if (showSpinner) {
      setIsLoadingDynamics(true);
    }

    isFetchingDynamics.current = true;
    setError(null);
    try {
      const params = {
        date_from: format(activeDateRange.from, 'yyyy-MM-dd'),
        date_to: format(activeDateRange.to, 'yyyy-MM-dd'),
        call_classification_type: selectedClassificationType,
        call_type: callType === 'all' ? undefined : callType
      };
      const data = await fetchCallDynamics(params);
      setCallDynamics(data ?? []);
    } catch {
      setError('Не удалось загрузить динамику звонков.');
    } finally {
      isFetchingDynamics.current = false;
      if (showSpinner) {
        setIsLoadingDynamics(false);
      }
    }
  }, [activeDateRange, callType, selectedClassificationType]);

  const loadFunnel = useCallback(async (showSpinner = true) => {
    if (USE_CUSTOM_FUNNEL_TENANTS.has(TENANT_ID)) {
      return;
    }

    if (!activeDateRange?.from || !activeDateRange?.to || isFetchingFunnel.current) {
      return;
    }

    if (showSpinner) {
      setIsLoadingFunnel(true);
    }

    isFetchingFunnel.current = true;
    setError(null);
    try {
      const params = {
        date_from: format(activeDateRange.from, 'yyyy-MM-dd'),
        date_to: format(activeDateRange.to, 'yyyy-MM-dd'),
        call_type: callType === 'all' ? undefined : callType
      };
      const data = await fetchBookingFunnel(params);
      setBookingFunnel(data ?? null);
    } catch {
      setError('Не удалось загрузить воронку целевых действий.');
    } finally {
      isFetchingFunnel.current = false;
      if (showSpinner) {
        setIsLoadingFunnel(false);
      }
    }
  }, [activeDateRange, callType]);

  const loadConversionDynamics = useCallback(async () => {
    if (!activeDateRange?.from || !activeDateRange?.to || isFetchingConversion.current) {
      return;
    }

    isFetchingConversion.current = true;
    try {
      const params = {
        date_from: format(activeDateRange.from, 'yyyy-MM-dd'),
        date_to: format(activeDateRange.to, 'yyyy-MM-dd'),
        call_type: callType === 'all' ? undefined : callType
      };
      const data = await fetchConversionDynamics(params);
      setConversionDynamics(Array.isArray(data) ? data : []);
    } catch {
      setConversionDynamics([]);
    } finally {
      isFetchingConversion.current = false;
    }
  }, [activeDateRange, callType]);

  useEffect(() => {
    loadClassificationTypes();
  }, [loadClassificationTypes]);

  useEffect(() => {
    loadCallDynamics();
  }, [loadCallDynamics]);

  useEffect(() => {
    loadFunnel();
  }, [loadFunnel]);

  useEffect(() => {
    loadConversionDynamics();
  }, [loadConversionDynamics]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      loadCallDynamics(false);
      loadFunnel(false);
      loadConversionDynamics();
    }, GRAPH_REFRESH_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [loadCallDynamics, loadFunnel, loadConversionDynamics]);

  const maxCalls = useMemo(() => {
    if (!callDynamics.length) return 1;
    return Math.max(
      ...callDynamics.map((point) =>
        Math.max(point.calls ?? 0, point.incomingCalls ?? 0, point.outgoingCalls ?? 0)
      )
    );
  }, [callDynamics]);

  const dynamicsUnitLabel = useMemo(() => {
    const granularity = callDynamics?.[0]?.granularity;
    if (granularity === 'minute') return 'минуту';
    if (granularity === 'hour') return 'час';
    return 'день';
  }, [callDynamics]);

  const lineChartData = useMemo(
    () =>
      (callDynamics ?? []).map((point) => {
        const totalCalls = point.calls ?? 0;
        const incomingCalls = point.incomingCalls ?? 0;
        const outgoingCalls = point.outgoingCalls ?? 0;
        return {
          label: formatCallDynamicsLabel(point, timeZone),
          calls: totalCalls,
          incomingCalls,
          outgoingCalls
        };
      }),
    [callDynamics, timeZone]
  );

  const funnelSteps = useMemo(() => {
    const applicable = bookingFunnel?.applicableCalls ?? 0;
    const attempts = bookingFunnel?.bookingAttempts ?? 0;
    const successes = bookingFunnel?.bookingSuccesses ?? 0;

    return [
      {
        key: 'applicable',
        stage: 'applicable',
        title: 'Целевое действие применимо',
        value: applicable,
        ratio: 100,
        color: '#2563eb'
      },
      {
        key: 'attempts',
        stage: 'attempt',
        title: 'Попытка довести до результата',
        value: attempts,
        ratio: bookingFunnel?.attemptRate ?? (applicable > 0 ? (attempts * 100) / applicable : 0),
        ratioLabel: `Конверсия: ${(bookingFunnel?.attemptRate ?? 0).toFixed(1)}% от применимых`,
        color: '#f59e0b'
      },
      {
        key: 'successes',
        stage: 'success',
        title: 'Целевое действие достигнуто',
        value: successes,
        ratio: bookingFunnel?.successRateOfAttempts ?? (attempts > 0 ? (successes * 100) / attempts : 0),
        ratioLabel: `Конверсия: ${(bookingFunnel?.successRateOfAttempts ?? 0).toFixed(1)}% от попыток · ${(bookingFunnel?.successRateOfApplicable ?? 0).toFixed(1)}% от применимых`,
        color: '#22c55e'
      }
    ];
  }, [bookingFunnel]);

  const conversionChartData = useMemo(
    () =>
      (conversionDynamics ?? []).map((point) => ({
        label: formatCallDynamicsLabel(point, timeZone),
        conversionRate: typeof point.conversionRate === 'number' ? point.conversionRate : Number(point.conversionRate) || 0,
        applicableCalls: point.applicableCalls ?? 0,
        bookingAttempts: point.bookingAttempts ?? 0,
        bookingSuccesses: point.bookingSuccesses ?? 0
      })),
    [conversionDynamics, timeZone]
  );

  return (
    <div className="flex flex-col gap-6">
      <div className="grid gap-6 lg:grid-cols-3">
      <Card className={USE_CUSTOM_FUNNEL_TENANTS.has(TENANT_ID) ? 'lg:col-span-3' : 'lg:col-span-2'}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Динамика звонков
            </CardTitle>
            <CardDescription>Всего звонков {dateRangeLabel}</CardDescription>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-3">
            <label className="text-sm text-muted-foreground">
              Тема:
              <select
                value={selectedClassificationType}
                onChange={(event) => setSelectedClassificationType(event.target.value)}
                className="ml-2 rounded-md border bg-background px-2 py-1 text-sm text-foreground"
              >
                <option value="">Все</option>
                {classificationTypes.map((type) => (
                  <option key={type} value={type}>
                    {formatClassificationType(type)}
                  </option>
                ))}
              </select>
            </label>
            <ChartModeToggle chartType={chartType} onChange={setChartType} />
            <ButtonReload loading={isLoadingDynamics} onReload={loadCallDynamics} />
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              {error}
            </div>
          )}
          {chartType === 'conversion' && (
            <div className="space-y-2">
              {conversionChartData.length === 0 ? (
                <p className="text-sm text-muted-foreground">Нет данных о конверсии за выбранный период.</p>
              ) : (
                <div className="relative h-64 w-full min-w-0 rounded-md bg-muted/30 px-2 py-4">
                  <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={160}>
                    <ReLineChart data={conversionChartData} margin={{ top: 12, right: 24, left: 0, bottom: 32 }}>
                      <CartesianGrid strokeDasharray="4 4" stroke="rgba(148,163,184,0.25)" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis
                        width={60}
                        domain={[0, 100]}
                        tickFormatter={(value) => `${value}%`}
                        tick={{ fontSize: 11 }}
                      />
                      <RechartsTooltip
                        wrapperStyle={tooltipWrapperStyle}
                        content={<ConversionTooltip />}
                      />
                      <Legend wrapperStyle={{ fontSize: '12px' }} />
                      <Line
                        type="monotone"
                        dataKey="conversionRate"
                        name="Конверсия в целевое действие"
                        stroke="#16a34a"
                        strokeWidth={2}
                        dot={{ r: 2 }}
                        activeDot={{ r: 4 }}
                      >
                        <LabelList
                          dataKey="conversionRate"
                          content={createValueLabelRenderer('#16a34a', (value) =>
                            typeof value === 'number' ? `${value.toFixed(1)}%` : value
                          )}
                        />
                      </Line>
                    </ReLineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          )}
          {chartType !== 'conversion' && callDynamics.length === 0 && !isLoadingDynamics && (
            <p className="text-sm text-muted-foreground">Нет данных для отображения.</p>
          )}
          {chartType !== 'conversion' && callDynamics.length > 0 && (
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground">
                Пик нагрузки: {maxCalls.toLocaleString()} звонков/{dynamicsUnitLabel}
              </div>
              {chartType === 'bar' ? (
                <div className="space-y-2">
                  <div className="relative h-64 w-full min-w-0 rounded-md bg-muted/30 px-2 py-4">
                    <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={160}>
                      <BarChart data={lineChartData} margin={{ top: 12, right: 24, left: 0, bottom: 32 }}>
                        <CartesianGrid strokeDasharray="4 4" stroke="rgba(148,163,184,0.25)" />
                        <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                        <YAxis
                          width={60}
                          domain={[0, Math.ceil(1.1 * maxCalls)]}
                          tickFormatter={callsAxisFormatter}
                          tick={{ fontSize: 11 }}
                        />
                        <RechartsTooltip
                          wrapperStyle={tooltipWrapperStyle}
                          content={<ChartTooltip formatter={callDynamicsTooltipFormatter} />}
                        />
                        <Legend wrapperStyle={{ fontSize: '12px' }} />
                        <Bar
                          dataKey="calls"
                          name="Звонки"
                          fill="#2563eb"
                          radius={[4, 4, 0, 0]}
                        >
                          <LabelList
                            dataKey="calls"
                            content={createValueLabelRenderer('#0f172a', callsAxisFormatter)}
                          />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="relative h-64 w-full min-w-0 rounded-md bg-muted/30 px-2 py-4">
                    <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={160}>
                      <ReLineChart data={lineChartData} margin={{ top: 12, right: 24, left: 0, bottom: 32 }}>
                        <CartesianGrid strokeDasharray="4 4" stroke="rgba(148,163,184,0.25)" />
                        <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                        <YAxis
                          width={60}
                          domain={[0, Math.ceil(1.1 * maxCalls)]}
                          tickFormatter={callsAxisFormatter}
                          tick={{ fontSize: 11 }}
                        />
                        <RechartsTooltip
                          wrapperStyle={tooltipWrapperStyle}
                          content={<ChartTooltip formatter={callDynamicsTooltipFormatter} />}
                        />
                        <Legend wrapperStyle={{ fontSize: '12px' }} />
                        <Line
                          type="monotone"
                          dataKey="calls"
                          name="Звонки"
                          stroke="#2563eb"
                          strokeWidth={2}
                          dot={{ r: 2 }}
                          activeDot={{ r: 4 }}
                        >
                          <LabelList
                            dataKey="calls"
                            content={createValueLabelRenderer('#2563eb', callsAxisFormatter)}
                          />
                        </Line>
                      </ReLineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {!USE_CUSTOM_FUNNEL_TENANTS.has(TENANT_ID) && (
      <Card>
        <CardHeader className="space-y-0 pb-2">
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <ArrowDown className="h-5 w-5" />
                Воронка целевых действий
              </CardTitle>
              <CardDescription>Применимые звонки → попытки → достигнутое действие</CardDescription>
            </div>
            <ButtonReload loading={isLoadingFunnel} onReload={loadFunnel} />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoadingFunnel && <p className="text-sm text-muted-foreground">Загрузка...</p>}
          {!isLoadingFunnel && !bookingFunnel && (
            <p className="text-sm text-muted-foreground">Нет данных по воронке.</p>
          )}

          <div className="space-y-3">
            {funnelSteps.map((step, index) => (
              <div key={step.key} className="space-y-2">
                {index > 0 && step.ratioLabel && (
                  <div className="text-center text-xs text-muted-foreground">
                    {step.ratioLabel}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => onOpenCallJournal?.({ funnel_stage: step.stage })}
                  title="Показать эти звонки в журнале"
                  className="block w-full rounded-md border bg-muted/20 p-3 text-left transition-colors hover:bg-muted/50 focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{step.title}</span>
                    <span className="text-sm font-semibold">{step.value.toLocaleString('ru-RU')}</span>
                  </div>
                  <div className="mt-2 h-2 rounded-full bg-gray-200 dark:bg-gray-800">
                    <div
                      className="h-2 rounded-full"
                      style={{
                        width: `${Math.max(0, Math.min(100, step.ratio))}%`,
                        backgroundColor: step.color
                      }}
                    />
                  </div>
                </button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
      )}
      </div>
      <ObjectionsChart
        dateRange={activeDateRange}
        callType={callType}
        onOpenCallJournal={onOpenCallJournal}
      />
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <ComplaintsNegativityChart dateRange={dateRange} callType={callType} />
        </div>
        <CallTopicsChart dateRange={dateRange} callType={callType} />
      </div>
      {TENANT_ID === 'mesoreal' && (
        <MesorealDashboardWidgets dateRange={activeDateRange} callType={callType} />
      )}
      {TENANT_ID === 'electrobike' && (
        <ElectrobikeDashboardWidgets
          dateRange={activeDateRange}
          callType={callType}
          onOpenCallJournal={onOpenCallJournal}
        />
      )}
      {TENANT_ID === 'bely_medved' && (
        <BelyMedvedDashboardWidgets
          dateRange={activeDateRange}
          callType={callType}
          onOpenCallJournal={onOpenCallJournal}
        />
      )}
      {TENANT_ID === 'dental_depo' && (
        <DentalDepoDashboardWidgets
          dateRange={activeDateRange}
          callType={callType}
          onOpenCallJournal={onOpenCallJournal}
        />
      )}
      {TENANT_ID === 'plus' && (
        <PlusDashboardWidgets
          dateRange={activeDateRange}
          callType={callType}
          onOpenCallJournal={onOpenCallJournal}
        />
      )}
    </div>
  );
};

const ConversionTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) {
    return null;
  }

  const point = payload[0]?.payload ?? {};

  return (
    <div
      className="rounded-md border px-3 py-2 text-sm shadow-md"
      style={{
        backgroundColor: 'hsl(var(--card))',
        color: 'hsl(var(--card-foreground))',
        borderColor: 'hsl(var(--border))'
      }}
    >
      <div className="mb-1 font-medium">{label}</div>
      <div>Конверсия: {Number(point.conversionRate ?? 0).toFixed(1)}%</div>
      <div>Применимые: {Number(point.applicableCalls ?? 0).toLocaleString('ru-RU')}</div>
      <div>Попытки результата: {Number(point.bookingAttempts ?? 0).toLocaleString('ru-RU')}</div>
      <div>Достигнуто: {Number(point.bookingSuccesses ?? 0).toLocaleString('ru-RU')}</div>
    </div>
  );
};

const ButtonReload = ({ loading, onReload }) => (
  <button
    type="button"
    onClick={onReload}
    disabled={loading}
    className="inline-flex items-center rounded-md border px-3 py-1 text-sm text-muted-foreground hover:bg-muted disabled:opacity-50"
  >
    <Filter className="mr-2 h-4 w-4" />
    {loading ? 'Обновление...' : 'Обновить'}
  </button>
);

const CHART_MODES = [
  { id: 'bar', label: 'Столбцы', icon: BarChart3 },
  { id: 'line', label: 'Линия', icon: LineChart },
  { id: 'conversion', label: 'Конверсия', icon: TrendingUp }
];

const ChartModeToggle = ({ chartType, onChange }) => (
  <div className="inline-flex rounded-md border">
    {CHART_MODES.map((mode, index) => {
      const Icon = mode.icon;
      const isActive = chartType === mode.id;
      return (
        <button
          key={mode.id}
          type="button"
          onClick={() => onChange(mode.id)}
          className={`inline-flex items-center px-3 py-1 text-sm ${
            index === 0 ? 'rounded-l-md' : ''
          } ${index === CHART_MODES.length - 1 ? 'rounded-r-md' : ''} ${
            isActive ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'
          }`}
        >
          <Icon className="mr-2 h-4 w-4" />
          {mode.label}
        </button>
      );
    })}
  </div>
);

export default GeneralStatistics;
